import { useEffect, useRef, useState } from 'react';
import { StyledProcessPurchaseForm } from './style';
import { Form, FormInstance } from 'antd';
import { Checkbox, CustomGrid, Input, TextArea } from 'ui';
import { useIntl } from 'react-intl';
import useQueryApiClient from 'utils/useQueryApiClient';
import { StringifyOptions } from 'querystring';
import { useAuth } from 'hooks/useAuth';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import useWindowSize from 'utils/useWindowSize';
import { AdresSearch, AMKResponse } from '../../../../ui/AdressSearch';
import { useWatch } from 'antd/es/form/Form';

export interface PersonalInfo {
  isLegalPerson: boolean;
  name?: string;
  client_surname?: string;
  company_name?: StringifyOptions;
  registration_number?: string;
  PVN_registration_number?: string;
  country?: string;
  city: string;
  address: string;
  post_index: string;
  e_mail: StringifyOptions;
  phone_number: StringifyOptions;
  comment?: StringifyOptions;
  checked?: boolean;
}

interface ProcessPurchaseFormProps {
  userForm: FormInstance;
}

const SESSION_STORAGE_KEY = 'processPurchaseFormValues';

const ProcessPurchaseForm = ({ userForm }: ProcessPurchaseFormProps) => {
  const [isOpenAddressSearch, setIsOpenAddressSearch] = useState(false);
  const intl = useIntl();
  const [_, windowWidth] = useWindowSize();
  const [isLegalPerson, setIsLegalPerson] = useState(false);
  const watchedValues = useWatch([], { form: userForm });

  const { refetch } = useQueryApiClient({
    request: {
      url: '/api/v2/orders/lastOrderByUser',
      method: 'GET',
      disableOnMount: true,
    },
    onSuccess: (response: PersonalInfo) => {
      userForm.setFieldsValue(response);
      setIsLegalPerson(response.isLegalPerson);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(response));
    },
  });

  useEffect(() => {
    const storedRaw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!storedRaw) {
      refetch();
    } else {
      try {
        const parsed: PersonalInfo = JSON.parse(storedRaw);
        if (parsed && typeof parsed === 'object') {
          parsed['isLegalPerson'] = false;
          userForm.setFieldsValue(parsed);
          userForm.setFieldValue('comment', null);
        }
      } catch (e) {
        refetch();
      }
    }
  }, []);

  const handleFormChange = (changedValues: any, allValues: PersonalInfo) => {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(allValues));
  };

  const isLegalPersonChange = (isLegalPerson: boolean) => {
    setIsLegalPerson(isLegalPerson);
  };

  const inOneRow = windowWidth > 576 ? 2 : 1;

  useEffect(() => {
    if (!watchedValues) return;

    const cleaned = Object.fromEntries(
      Object.entries(watchedValues)
        .filter(
          ([_, v]) =>
            v !== undefined &&
            v !== null &&
            !(typeof v === 'string' && v.trim() === '')
        )
        .map(([k, v]) => (typeof v === 'string' ? [k, v.trim()] : [k, v]))
    );

    if (Object.keys(cleaned).length === 0) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } else {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(cleaned));
    }
  }, [watchedValues]);

  return (
    <StyledProcessPurchaseForm>
      <Form form={userForm} layout="vertical" onValuesChange={handleFormChange}>
        <CustomGrid inOneRow={inOneRow}>
          <Input
            className="purchase-form-item"
            label={intl.formatMessage({ id: 'process_purchase.name' })}
            name="name"
            validations={'required'}
          />
          <Input
            className="purchase-form-item"
            label={intl.formatMessage({
              id: 'process_purchase.client_surname',
            })}
            name="client_surname"
            validations={'required'}
          />
          <Input
            className="purchase-form-item"
            label={intl.formatMessage({ id: 'process_purchase.country' })}
            name="country"
            validations={!isLegalPerson ? 'required' : []}
            disabled={!isLegalPerson}
          />
          <Input
            className="purchase-form-item"
            label={intl.formatMessage({ id: 'process_purchase.city' })}
            name="city"
            validations={!isLegalPerson ? 'required' : []}
            disabled
          />
          <Input
            label={intl.formatMessage({ id: 'profile.adress' })}
            className={'purchase-form-item'}
            size={'large'}
            name={'address'}
            validations={!isLegalPerson ? 'required' : []}
            onClick={() => setIsOpenAddressSearch(true)}
            readOnly
          />
          <Input
            className="purchase-form-item"
            label={intl.formatMessage({
              id: 'process_purchase.post_index',
            })}
            name="post_index"
            disabled
            validations={'postIndex'}
          />
          <Input
            className="purchase-form-item"
            label={intl.formatMessage({ id: 'process_purchase.e_mail' })}
            name="e_mail"
            validations={['email', 'required']}
          />
          <Input
            className="purchase-form-item"
            label={intl.formatMessage({
              id: 'process_purchase.phone_number',
            })}
            name="phone_number"
            validations={['phoneNumber', 'required']}
          />
        </CustomGrid>

        <Checkbox
          onChange={(e: CheckboxChangeEvent) =>
            isLegalPersonChange(e.target.checked)
          }
          itemClassName="purchase-form-checkbox-item"
          name="isLegalPerson"
          value={'process_purchase.buy_as_legal_person'}
          checked={isLegalPerson}
          label={intl.formatMessage({
            id: 'process_purchase.buy_as_legal_person',
          })}
        />
        {isLegalPerson && (
          <CustomGrid inOneRow={inOneRow}>
            <Input
              className="purchase-form-item"
              label={intl.formatMessage({
                id: 'process_purchase.company_name',
              })}
              name="company_name"
              validations={'required'}
            />
            <Input
              className="purchase-form-item"
              label={intl.formatMessage({
                id: 'process_purchase.registration_number',
              })}
              name="registration_number"
              validations={'required'}
            />
            <Input
              className="purchase-form-item"
              label={intl.formatMessage({
                id: 'process_purchase.PVN_registration_number',
              })}
              name="PVN_registration_number"
              validations={'required'}
            />
            <Input
              className="purchase-form-item"
              label={intl.formatMessage({ id: 'process_purchase.city' })}
              name="company_city"
              validations={'required'}
            />
            <Input
              className="purchase-form-item"
              label={intl.formatMessage({ id: 'process_purchase.address' })}
              name="company_address"
              validations={'required'}
            />
            <Input
              className="purchase-form-item"
              label={intl.formatMessage({
                id: 'process_purchase.post_index',
              })}
              name="company_post_index"
              validations={['postIndex', 'required']}
            />
          </CustomGrid>
        )}

        <CustomGrid inOneRow={1}>
          <div className={'purchase-form-title'}>
            {intl.formatMessage({ id: 'process_purchase.comment' })}
          </div>
          <TextArea name={'comment'} />
        </CustomGrid>
        <AdresSearch
          isOpen={isOpenAddressSearch}
          setIsOpen={setIsOpenAddressSearch}
          onItemSelect={(event: AMKResponse) => {
            if (event.data?.code) {
              if (event.data?.irAdrese) {
                userForm.setFieldValue(
                  'city',
                  event.data?.pilName ?? event.data?.novName
                );
                userForm.setFieldValue('post_index', event.data?.zipCode);
                userForm.setFieldValue(
                  'address',
                  `${event.data?.address}, ${event.data?.zipCode}`
                );
                userForm.setFieldValue('country', 'Latvija');

                setIsOpenAddressSearch(false);
              } else {
                alert(
                  intl.formatMessage({ id: 'general.warning_show_full_adress' })
                );
              }
            }
          }}
        />
      </Form>
    </StyledProcessPurchaseForm>
  );
};

export default ProcessPurchaseForm;
